import { useMediaQuery } from "react-responsive";

import { Card, Heading, MediaTile, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { screenSizes } from "consts";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { capitalize, checkForNaStringValue, trackEvent } from "utils";

import styles from "./ineligible-write-out-card.module.scss";

export const IneligibleWriteOutCard = ({
  referral,
  showGallery,
}: {
  referral: KeystoneReferral;
  showGallery: () => void;
}) => {
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const maxPhotosInRow = isMobile ? 3 : 5;
  const {
    category,
    groupedIssueStatements,
    groupedReasonings,
    inspectionReviewAssetIds,
    issueStatement,
    locationDetail,
    reasoning,
  } = referral;

  let writeOutHeading: string | null =
    issueStatement?.replace(/^.*? - /, "") ?? "";
  if (groupedIssueStatements?.length) {
    writeOutHeading = "Multiple issues";
  }
  writeOutHeading = checkForNaStringValue(writeOutHeading);
  const reasoningDisplay = checkForNaStringValue(reasoning ?? "");
  let reasonings: string[] = [];
  if (groupedReasonings?.length) {
    reasonings = groupedReasonings;
  } else if (reasoningDisplay) {
    reasonings = [reasoningDisplay];
  }
  const locationDisplay = checkForNaStringValue(locationDetail ?? "");
  const categoryDisplay = checkForNaStringValue(category ?? "");
  const formattedLocationDisplay = locationDisplay
    ? ` • ${locationDisplay}`
    : "";
  const subHeading: string = categoryDisplay
    ? `${categoryDisplay}${formattedLocationDisplay}`
    : "";

  const renderAssetList = () => {
    if (!inspectionReviewAssetIds?.length) return null;

    const visibleAssetIds =
      inspectionReviewAssetIds.length > maxPhotosInRow
        ? inspectionReviewAssetIds.slice(0, maxPhotosInRow - 1)
        : inspectionReviewAssetIds;

    return (
      <div className={styles.fileListContainer}>
        <Span bold>Inspection photos</Span>
        <ul className={styles.fileList}>
          {visibleAssetIds.map((assetId) => (
            <li className={styles.fileListItem} key={assetId}>
              <MediaTile
                source={`/api/inspection/image/${assetId}`}
                orientation="landscape"
                imageFit="cover"
                description={`Inspection asset: ${assetId}`}
                onClick={() => {
                  trackEvent(
                    "Inspection ineligible write-outs: Photo gallery opened",
                    {
                      numberOfPhotos: inspectionReviewAssetIds.length,
                      referral,
                    },
                  );
                  showGallery();
                }}
                className={styles.fileTile}
              />
            </li>
          ))}
          {inspectionReviewAssetIds.length > maxPhotosInRow ? (
            <li className={styles.fileTileOverflow}>
              <MediaTile.Overflow
                onClick={() => {
                  trackEvent(
                    "Inspection ineligible write-outs: Photo gallery opened",
                    {
                      numberOfPhotos: inspectionReviewAssetIds.length,
                      referral,
                    },
                  );
                  showGallery();
                }}
                text={`+${
                  inspectionReviewAssetIds.length - (maxPhotosInRow - 1)
                } more`}
              />
            </li>
          ) : null}
        </ul>
      </div>
    );
  };

  return (
    <Card>
      <Stack gap="sm">
        <div className={styles.heading}>
          <div className={styles.titleAndSubheading}>
            {writeOutHeading ? (
              <Heading size="sm">{capitalize(writeOutHeading)}</Heading>
            ) : null}
            <Span color="quiet" size="sm">
              {subHeading}
            </Span>
          </div>
        </div>

        {reasonings.length ? (
          <div className={styles.reasoning}>
            <Span bold>Why is this an issue?</Span>
            {reasonings.map((reasoningString, idx) => (
              <Span key={idx}>{reasoningString}</Span>
            ))}
          </div>
        ) : null}

        {renderAssetList()}
      </Stack>
    </Card>
  );
};
